/**
 * Model Parameter Conversion and Validation Utilities
 * Handles conversion between frontend ModelParameters and backend expected formats
 */

import { ModelParameters } from '../features/training/components/ModelConfiguration';

// Backend expects exact MDL format from training_backend_test_2.py
export interface BackendModelParameters {
  MODE: string;     // Model type: "Dense", "CNN", "LSTM", etc.
  LAY?: number;     // Number of layers
  N?: number;       // Number of neurons/filters per layer
  EP?: number;      // Number of epochs
  ACTF?: string;    // Activation function
  K?: number;       // Kernel size (CNN only)
  KERNEL?: string;  // Kernel type (SVR only)
  C?: number;       // C parameter (SVR only)
  EPSILON?: number; // Epsilon (SVR only)
  N_ESTIMATORS?: number;  // Number of estimators (LGBMR only)
  LEARNING_RATE?: number; // Learning rate (LGBMR only)
  MAX_DEPTH?: number;     // Max depth (LGBMR only)
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  correctedParams?: ModelParameters;
}

/**
 * Convert frontend ModelParameters to backend MDL format
 * Matches exact structure expected by training_backend_test_2.py
 */
export function convertUIToBackendFormat(uiParams: ModelParameters): BackendModelParameters {
  
  // Create backend params matching MDL class structure
  const backendParams: BackendModelParameters = {
    MODE: uiParams.MODE
  };
  
  // Ensure we have a valid model type
  if (!uiParams.MODE) {
    throw new Error('Model type (MODE) is required');
  }
  
  
  // Copy parameters based on model type
  switch (uiParams.MODE) {
    case 'Dense':
      backendParams.LAY = uiParams.LAY;
      backendParams.N = uiParams.N;
      backendParams.EP = uiParams.EP;
      backendParams.ACTF = uiParams.ACTF; // Keep original format (e.g., "ReLU" not "relu")
      break;
      
    case 'CNN':
      backendParams.LAY = uiParams.LAY;
      backendParams.N = uiParams.N;
      backendParams.K = uiParams.K;
      backendParams.EP = uiParams.EP;
      backendParams.ACTF = uiParams.ACTF;
      break;
      
    case 'LSTM':
    case 'AR LSTM':
      backendParams.LAY = uiParams.LAY;
      backendParams.N = uiParams.N;
      backendParams.EP = uiParams.EP;
      backendParams.ACTF = uiParams.ACTF;
      break;
      
    case 'SVR_dir':
    case 'SVR_MIMO':
      backendParams.KERNEL = uiParams.KERNEL;
      backendParams.C = uiParams.C;
      backendParams.EPSILON = uiParams.EPSILON;
      break;
      
    case 'LGBMR':
      backendParams.N_ESTIMATORS = uiParams.N_ESTIMATORS;
      backendParams.LEARNING_RATE = uiParams.LEARNING_RATE;
      backendParams.MAX_DEPTH = uiParams.MAX_DEPTH;
      break;

    case 'LIN':
      // Linear model has no additional parameters
      break;
  }
  
  return backendParams;
}

// Removed mapActivationFunction - no longer needed since we keep original format

/**
 * Validate ModelParameters before conversion
 */
export function validateModelParameters(params: ModelParameters): ValidationResult {
  const result: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: [],
    correctedParams: { ...params }
  };

  // Validate model type
  const validModes = ['Dense', 'CNN', 'LSTM', 'AR LSTM', 'SVR_dir', 'SVR_MIMO', 'LGBMR', 'LIN'];
  if (!validModes.includes(params.MODE)) {
    result.errors.push(`Invalid model type: ${params.MODE}. Must be one of: ${validModes.join(', ')}`);
    result.isValid = false;
  }

  // Validate neural network parameters
  if (['Dense', 'CNN', 'LSTM', 'AR LSTM'].includes(params.MODE)) {
    // Validate layers
    if (params.LAY !== undefined) {
      if (params.LAY < 1 || params.LAY > 10) {
        result.warnings.push('Number of layers should be between 1 and 10');
        result.correctedParams!.LAY = Math.max(1, Math.min(params.LAY, 10));
      }
    } else {
      result.errors.push('Number of layers is required for neural network models');
      result.isValid = false;
    }

    // Validate neurons/filters
    if (params.N !== undefined) {
      if (params.N < 1 || params.N > 2048) {
        result.warnings.push('Number of neurons/filters should be between 1 and 2048');
        result.correctedParams!.N = Math.max(1, Math.min(params.N, 2048));
      }
    } else if (params.MODE !== 'LIN') {
      result.errors.push('Number of neurons/filters is required');
      result.isValid = false;
    }

    // Validate epochs
    if (params.EP !== undefined) {
      if (params.EP < 1 || params.EP > 1000) {
        result.warnings.push('Number of epochs should be between 1 and 1000');
        result.correctedParams!.EP = Math.max(1, Math.min(params.EP, 1000));
      }
    } else {
      result.errors.push('Number of epochs is required for neural network models');
      result.isValid = false;
    }

    // Validate activation function
    const validActivations = ['ReLU', 'Sigmoid', 'Tanh', 'Linear', 'Softmax', 'Keine'];
    if (!params.ACTF || !validActivations.includes(params.ACTF)) {
      result.errors.push('Activation function is required for neural network models');
      result.isValid = false;
    }

    // CNN specific validation
    if (params.MODE === 'CNN') {
      if (params.K !== undefined) {
        if (params.K < 1 || params.K > 11 || params.K % 2 === 0) {
          result.warnings.push('Kernel size should be an odd number between 1 and 11');
          result.correctedParams!.K = params.K % 2 === 0 ? params.K + 1 : Math.max(1, Math.min(params.K, 11));
        }
      } else {
        result.errors.push('Kernel size is required for CNN models');
        result.isValid = false;
      }
    }
  }

  // Validate SVR parameters
  if (['SVR_dir', 'SVR_MIMO'].includes(params.MODE)) {
    // Validate kernel type
    const validKernels = ['linear', 'poly', 'rbf', 'sigmoid'];
    if (!params.KERNEL || !validKernels.includes(params.KERNEL.toLowerCase())) {
      result.errors.push(`Kernel type is required for SVR models. Must be one of: ${validKernels.join(', ')}`);
      result.isValid = false;
    }

    // Validate C parameter
    if (params.C !== undefined) {
      if (params.C <= 0) {
        result.warnings.push('C parameter should be positive');
        result.correctedParams!.C = Math.max(0.001, params.C);
      }
    } else {
      result.errors.push('C parameter is required for SVR models');
      result.isValid = false;
    }

    // Validate epsilon
    if (params.EPSILON !== undefined) {
      if (params.EPSILON < 0 || params.EPSILON > 1) {
        result.warnings.push('Epsilon should be between 0 and 1');
        result.correctedParams!.EPSILON = Math.max(0, Math.min(params.EPSILON, 1));
      }
    } else {
      result.errors.push('Epsilon is required for SVR models');
      result.isValid = false;
    }
  }

  // Validate LGBMR parameters
  if (params.MODE === 'LGBMR') {
    if (params.N_ESTIMATORS !== undefined) {
      if (params.N_ESTIMATORS < 1 || params.N_ESTIMATORS > 10000) {
        result.warnings.push('Number of estimators should be between 1 and 10000');
        result.correctedParams!.N_ESTIMATORS = Math.max(1, Math.min(params.N_ESTIMATORS, 10000));
      }
    } else {
      result.errors.push('Number of estimators is required for LGBMR model');
      result.isValid = false;
    }

    if (params.LEARNING_RATE !== undefined) {
      if (params.LEARNING_RATE <= 0 || params.LEARNING_RATE > 1) {
        result.warnings.push('Learning rate should be between 0.001 and 1');
        result.correctedParams!.LEARNING_RATE = Math.max(0.001, Math.min(params.LEARNING_RATE, 1));
      }
    } else {
      result.errors.push('Learning rate is required for LGBMR model');
      result.isValid = false;
    }

    if (params.MAX_DEPTH === undefined) {
      result.errors.push('Max depth is required for LGBMR model');
      result.isValid = false;
    }
  }

  return result;
}

/**
 * Get required fields for a specific model type
 */
export function getRequiredFields(modelType: string): string[] {
  switch (modelType) {
    case 'Dense':
      return ['LAY', 'N', 'EP', 'ACTF'];
    case 'CNN':
      return ['LAY', 'N', 'K', 'EP', 'ACTF'];
    case 'LSTM':
    case 'AR LSTM':
      return ['LAY', 'N', 'EP', 'ACTF'];
    case 'SVR_dir':
    case 'SVR_MIMO':
      return ['KERNEL', 'C', 'EPSILON'];
    case 'LGBMR':
      return ['N_ESTIMATORS', 'LEARNING_RATE', 'MAX_DEPTH'];
    case 'LIN':
      return [];
    default:
      return [];
  }
}

/**
 * Check if all required fields are filled
 */
export function areRequiredFieldsFilled(params: ModelParameters): boolean {
  const requiredFields = getRequiredFields(params.MODE);
  return requiredFields.every(field => {
    const value = params[field as keyof ModelParameters];
    return value !== undefined && value !== null && value !== '';
  });
}

/**
 * Get user-friendly field names for error messages
 */
export function getFieldDisplayName(field: string): string {
  const fieldNames: Record<string, string> = {
    'LAY': 'Number of Layers',
    'N': 'Neurons/Filters',
    'K': 'Kernel Size',
    'EP': 'Epochs',
    'ACTF': 'Activation Function',
    'KERNEL': 'Kernel Type',
    'C': 'C Parameter',
    'EPSILON': 'Epsilon',
    'N_ESTIMATORS': 'Number of Estimators',
    'LEARNING_RATE': 'Learning Rate',
    'MAX_DEPTH': 'Max Depth'
  };
  
  return fieldNames[field] || field;
}